import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiStar, FiEdit, FiTrash2, FiFilter, FiSearch, FiPlus,
  FiMessageCircle, FiImage, FiUsers, FiHeart
} from 'react-icons/fi';
import { 
  getReviewsAdmin, createReview, updateReview, deleteReview 
} from '../../utils/adminApi';
import ImageUpload from '../../components/admin/ImageUpload';

const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [formData, setFormData] = useState({
    type: 'whatsapp',
    content: '',
    author: '',
    rating: 5,
    image: '',
    date: new Date().toISOString().split('T')[0],
    is_featured: false
  });
  
  const [imageUrl, setImageUrl] = useState('');

  useEffect(() => {
    fetchReviews();
  }, [selectedType, currentPage]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        per_page: 10,
        ...(selectedType && { type: selectedType })
      };
      
      const data = await getReviewsAdmin(params);
      setReviews(data.reviews);
      setTotalPages(data.pagination.pages);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        rating: parseInt(formData.rating),
        date: formData.date ? new Date(formData.date).toISOString() : new Date().toISOString()
      };

      if (editingReview) {
        await updateReview(editingReview.id, submitData);
      } else {
        await createReview(submitData);
      }
      
      setShowModal(false);
      setEditingReview(null);
      resetForm();
      fetchReviews();
    } catch (error) {
      console.error('Failed to save review:', error);
      alert(error.error || 'Failed to save review');
    }
  };

  const handleEdit = (review) => {
    setEditingReview(review);
    setFormData({
      type: review.type,
      content: review.content,
      author: review.author,
      rating: review.rating,
      image: review.image || '',
      date: review.date ? review.date.split('T')[0] : new Date().toISOString().split('T')[0],
      is_featured: review.is_featured
    });
    setImageUrl(review.image || '');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this review?')) {
      try {
        await deleteReview(id);
        fetchReviews();
      } catch (error) {
        console.error('Failed to delete review:', error);
        alert(error.error || 'Failed to delete review');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      type: 'whatsapp',
      content: '',
      author: '',
      rating: 5,
      image: '',
      date: new Date().toISOString().split('T')[0],
      is_featured: false
    });
    setImageUrl('');
  };

  const handleImageUpload = (data) => {
    // Handle S3 upload response (image_urls object) or direct image_url
    const imageUrl = data.image_urls?.original || data.image_urls?.medium || data.image_url || data.file_url;
    if (imageUrl) {
      setImageUrl(imageUrl);
      setFormData(prev => ({ ...prev, image: imageUrl }));
    }
  };

  const filteredReviews = reviews.filter(review =>
    review.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
    review.content.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getTypeColor = (type) => {
    switch(type) {
      case 'whatsapp': return 'bg-green-100 text-green-800';
      case 'meal': return 'bg-orange-100 text-orange-800';
      case 'client': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'whatsapp': return FiMessageCircle;
      case 'meal': return FiImage;
      case 'client': return FiUsers;
      default: return FiStar;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Reviews Management</h2>
          <p className="text-gray-600 mt-1">Manage client reviews and testimonials</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setEditingReview(null);
            setShowModal(true);
          }}
          className="btn-primary flex items-center space-x-2"
        >
          <FiPlus className="w-5 h-5" />
          <span>Add Review</span>
        </button>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by author or content..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 input-field w-full"
            />
          </div>
          <div className="relative">
            <FiFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <select
              value={selectedType}
              onChange={(e) => {
                setSelectedType(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-10 input-field"
            >
              <option value="">All Types</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="meal">Meal</option>
              <option value="client">Client</option>
            </select>
          </div>
        </div>
      </div>

      {/* Reviews Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm border border-primary-100 p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded mb-3"></div>
              <div className="h-3 bg-gray-200 rounded mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-2/3"></div>
            </div>
          ))
        ) : filteredReviews.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <FiStar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No reviews found</h3>
            <p className="text-gray-600 mb-4">
              {searchTerm || selectedType 
                ? 'Try adjusting your filters' 
                : 'Start by adding your first review'}
            </p>
            <button
              onClick={() => {
                resetForm();
                setEditingReview(null);
                setShowModal(true);
              }}
              className="btn-primary"
            >
              Add First Review
            </button>
          </div>
        ) : (
          filteredReviews.map((review) => {
            const TypeIcon = getTypeIcon(review.type);
            
            return (
              <motion.div
                key={review.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl shadow-sm border border-primary-100 overflow-hidden hover:shadow-md transition-shadow"
              >
                {/* Type Header */}
                <div className={`px-4 py-3 text-white font-medium flex items-center space-x-2 ${
                  review.type === 'whatsapp' ? 'bg-green-500' :
                  review.type === 'meal' ? 'bg-orange-500' :
                  'bg-blue-500'
                }`}>
                  <TypeIcon className="w-5 h-5" />
                  <span className="capitalize">{review.type} Review</span>
                  {review.is_featured && (
                    <FiStar className="w-4 h-4 ml-auto fill-current" />
                  )}
                </div>

                <div className="p-6">
                  {/* Rating */}
                  <div className="flex items-center text-amber-500 mb-3">
                    {[...Array(review.rating)].map((_, i) => (
                      <FiStar key={i} className="w-4 h-4 fill-current" />
                    ))}
                  </div>

                  {/* Content */}
                  <p className="text-sm text-gray-700 mb-4 line-clamp-3">
                    "{review.content}"
                  </p>

                  {/* Image Preview */}
                  {review.image && (
                    <div className="mb-4">
                      <img 
                        src={review.image} 
                        alt="Review" 
                        className="w-full h-32 object-cover rounded-lg"
                      />
                    </div>
                  )}

                  {/* Author and Date */}
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                    <span>{review.author}</span>
                    <span>{new Date(review.date).toLocaleDateString()}</span>
                  </div>

                  {/* Actions */}
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(review)}
                      className="flex-1 bg-gray-100 text-gray-700 py-2 px-3 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                    >
                      <FiEdit className="w-4 h-4 inline mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(review.id)}
                      className="bg-red-100 text-red-700 py-2 px-3 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium"
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex justify-center">
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            <span className="px-3 py-2 text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-bold mb-4">
                {editingReview ? 'Edit Review' : 'Add New Review'}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Review Type *
                    </label>
                    <select
                      required
                      value={formData.type}
                      onChange={(e) => setFormData({...formData, type: e.target.value})}
                      className="input-field"
                    >
                      <option value="whatsapp">WhatsApp</option>
                      <option value="meal">Meal</option>
                      <option value="client">Client</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rating (1-5) *
                    </label>
                    <input
                      type="number"
                      required
                      min="1"
                      max="5"
                      value={formData.rating}
                      onChange={(e) => setFormData({...formData, rating: e.target.value})}
                      className="input-field"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Author Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.author}
                    onChange={(e) => setFormData({...formData, author: e.target.value})}
                    className="input-field"
                    placeholder="e.g., Priya S."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Review Content *
                  </label>
                  <textarea
                    required
                    rows={4}
                    value={formData.content}
                    onChange={(e) => setFormData({...formData, content: e.target.value})}
                    className="input-field"
                    placeholder="Enter the review content..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Review Date
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    className="input-field"
                  />
                </div>

                {/* Image Upload (Optional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Review Image (Optional)
                  </label>
                  <ImageUpload
                    endpoint="/admin/upload/image"
                    onUploadSuccess={handleImageUpload}
                    currentImage={imageUrl}
                    onImageRemove={() => {
                      setImageUrl('');
                      setFormData(prev => ({ ...prev, image: '' }));
                    }}
                    label="Upload Image"
                    category="reviews"
                    prefix="review"
                    maxSize={20}
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_featured"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData({...formData, is_featured: e.target.checked})}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="is_featured" className="ml-2 text-sm text-gray-700">
                    Featured Review
                  </label>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 btn-primary"
                  >
                    {editingReview ? 'Update Review' : 'Create Review'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingReview(null);
                      resetForm();
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminReviews;

